import { useMutation } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { handleDownload } from "../../downloads/utils";
import { backendService } from "../../services/backend.service";
import { CostDeepDiveUnit, type OtMode, type StatsMode } from "../../utils/models";
import type { DifferenceMode } from "./ScheduleVsActual/types";

export const useDownloadCostDeepDive = () => {
	const { chosenDepartment } = useChosenDepartment();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async ({
			startDate,
			endDate,
			isActual,
			shiftHours,
			roles,
			jobs,
			laborCost,
			excludeHolidays,
			hideWage,
			includeLateScheduled,
			unit,
			otMode,
			estimateMissingWage,
		}: {
			startDate: DateTime;
			endDate?: DateTime;
			isActual: boolean;
			shiftHours?: string[];
			roles?: string[];
			jobs?: string[];
			laborCost?: boolean;
			excludeHolidays?: boolean;
			hideWage?: boolean;
			includeLateScheduled?: boolean;
			unit: CostDeepDiveUnit;
			otMode?: OtMode;
			estimateMissingWage: boolean;
		}) => {
			track("cost_deep_dive_download_start", { unit });
			const response = await backendService.downloadCostDeepDive({
				departmentId: chosenDepartment.department_id,
				startDate,
				endDate,
				isActual,
				laborCost,
				shiftHours,
				roles,
				jobs,
				excludeHolidays,
				hideWage,
				includeLateScheduled,
				unit,
				otMode,
				estimateMissingWage,
			});
			const unitSegment = unit === CostDeepDiveUnit.Hours ? "hours-" : "";
			const fileName = `cost-deep-dive-${unitSegment}${startDate.toFormat("yyyy-MM-dd")}${endDate ? `-to-${endDate.toFormat("yyyy-MM-dd")}` : ""}.xlsx`;
			handleDownload(response.data, fileName);
			track("cost_deep_dive_download_success", { unit });
		},
		onError: (e: Error, variables) => {
			trackError("cost_deep_dive_download_error", { error_message: e?.message, unit: variables.unit });
		},
	});
};

export const useDownloadScheduleVsActual = () => {
	const { chosenDepartment } = useChosenDepartment();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async ({
			startDate,
			endDate,
			metric,
			shiftHours,
			roles,
			jobs,
			laborCost,
			excludeHolidays,
			includeBreaks,
			hideWage,
			includeLateScheduled,
			wantedMode,
			diffMode,
			otMode,
			estimateMissingWage,
		}: {
			startDate: DateTime;
			endDate?: DateTime;
			metric: string;
			shiftHours?: string[];
			roles?: string[];
			jobs?: string[];
			laborCost?: boolean;
			excludeHolidays?: boolean;
			includeBreaks?: boolean;
			hideWage?: boolean;
			includeLateScheduled?: boolean;
			wantedMode?: StatsMode;
			diffMode?: DifferenceMode;
			otMode?: OtMode;
			estimateMissingWage: boolean;
		}) => {
			track("schedule_vs_actual_download_start");
			const response = await backendService.downloadScheduleVsActual({
				departmentId: chosenDepartment.department_id,
				startDate,
				endDate,
				metric,
				laborCost,
				shiftHours,
				roles,
				jobs,
				excludeHolidays,
				includeBreaks,
				hideWage,
				includeLateScheduled,
				wantedMode,
				diffMode,
				otMode,
				estimateMissingWage,
			});
			const fileName = `schedule-vs-actual-${metric}-${startDate.toFormat("yyyy-MM-dd")}${endDate ? `-to-${endDate.toFormat("yyyy-MM-dd")}` : ""}.xlsx`;
			handleDownload(response.data, fileName);
			track("schedule_vs_actual_download_success");
		},
		onError: (e: Error) => {
			trackError("schedule_vs_actual_download_error", { error_message: e?.message });
		},
	});
};
